/**
 * Canonical permission-filtered menu — the single source for every menu
 * surface (desktop `AsideMenu`, the mobile `BottomTabBar`, and the
 * slide-up `MobileMenuSheet`), so all three always agree on which
 * entries the current user can see.
 *
 * Policy (unchanged from the logic this was lifted out of `AsideMenu`):
 *   • permissions `ready`   → `filterMenuByPermissions` over MENU_CONFIG,
 *     with a fail-open operation gate: when the minimized snapshot omits
 *     the operationPermissions map entirely, every operation is treated
 *     as permitted; only an explicit denial hides an entry.
 *   • permissions `error`   → the UNFILTERED `MENU_CONFIG`, the same
 *     fallback the aside menu has always shown when the permission call
 *     fails (route gates still protect every page behind the entries).
 *   • otherwise (`loading`) → empty, so surfaces render nothing until
 *     the snapshot lands.
 */
import { useCallback, useMemo } from 'react';

import { usePermissions } from '../../store/PermissionContext';
import { filterMenuByPermissions } from './filterMenu';
import { MENU_CONFIG } from './menuConfig';
import type { MenuItem } from './types';

export function useFilteredMenu(): ReadonlyArray<MenuItem> {
  const {
    status: permissionStatus,
    hasPermissionForRoute,
    hasOperationPermission,
    hasOperationPermissionsLoaded,
  } = usePermissions();

  const operationPermissionGate = useCallback(
    (operationId: string): boolean => {
      if (!hasOperationPermissionsLoaded()) return true;
      return hasOperationPermission(operationId);
    },
    [hasOperationPermission, hasOperationPermissionsLoaded],
  );

  return useMemo<ReadonlyArray<MenuItem>>(() => {
    if (permissionStatus === 'ready') {
      return filterMenuByPermissions(
        MENU_CONFIG,
        hasPermissionForRoute,
        operationPermissionGate,
      );
    }
    if (permissionStatus === 'error') {
      return MENU_CONFIG;
    }
    return [];
  }, [permissionStatus, hasPermissionForRoute, operationPermissionGate]);
}
